import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

let isRefreshing = false;
let refreshWaiters = [];

function resolveWaiters(error) {
  refreshWaiters.forEach(({ resolve, reject }) => (error ? reject(error) : resolve()));
  refreshWaiters = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const isAuthEndpoint = originalRequest?.url?.includes("/auth/login") || originalRequest?.url?.includes("/auth/refresh");

    if (status !== 401 || isAuthEndpoint || originalRequest._retried) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Wait for the in-flight refresh to finish, then retry this request once.
      await new Promise((resolve, reject) => refreshWaiters.push({ resolve, reject }));
      originalRequest._retried = true;
      return api(originalRequest);
    }

    originalRequest._retried = true;
    isRefreshing = true;
    try {
      await api.post("/auth/refresh");
      isRefreshing = false;
      resolveWaiters(null);
      return api(originalRequest);
    } catch (refreshError) {
      isRefreshing = false;
      resolveWaiters(refreshError);
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function fileUrl(path) {
  if (!path) return null;
  return `${API}/files/download?path=${encodeURIComponent(path)}`;
}

export function downloadUrl(path, filename) {
  if (!path) return null;
  const params = new URLSearchParams({ path, disposition: "attachment" });
  if (filename) params.set("filename", filename);
  return `${API}/files/download?${params.toString()}`;
}

export async function downloadAndOpen(path, filename) {
  const res = await api.get("/files/download", { params: { path }, responseType: "blob" });
  const blobUrl = URL.createObjectURL(res.data);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename || "file";
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
}

export default api;
